import React from 'react';
import { Link } from 'react-router-dom';
import { useTabPermissions } from '../../context/TabPermissionContext';
import { ShieldX, LifeBuoy } from 'lucide-react';

/**
 * Route guard that checks tab-level permissions.
 *
 * Usage:  <TabGuard tabKey="reports"><ReportsPage /></TabGuard>
 *
 * • If permission data hasn't loaded yet, renders children (avoids flash).
 * • Admin users always pass (bypass in TabPermissionContext).
 * • If hidden, shows an "Access Restricted" message with a link to dashboard.
 */
const TabGuard = ({ tabKey, children }) => {
  const { isTabVisible, loaded } = useTabPermissions();

  // Permissions not yet loaded — render children to avoid flash of restricted screen
  if (!loaded) return children;

  // Permission granted
  if (isTabVisible(tabKey)) return children;

  // Blocked
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <div className="w-16 h-16 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center mb-5 ring-1 ring-indigo-100 dark:ring-indigo-500/20">
        <ShieldX className="w-8 h-8 text-indigo-500 dark:text-indigo-400" />
      </div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No access to this area</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mb-6">
        You don&apos;t have permission to view this page. If this is unexpected, contact your administrator or visit help for support options.
      </p>
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <Link
          to="/dashboard"
          className="inline-flex items-center justify-center px-5 py-2.5 bg-gradient-to-r from-operis-blue to-indigo-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-operis-blue/25 transition-all w-full sm:w-auto"
        >
          Go to Dashboard
        </Link>
        <Link
          to="/help"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-semibold rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 transition-colors w-full sm:w-auto"
        >
          <LifeBuoy className="w-4 h-4" aria-hidden />
          Help &amp; support
        </Link>
      </div>
    </div>
  );
};

export default TabGuard;
